import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ApiService, queryKeys } from '../services/api';
import {
	MegaphoneIcon,
	CpuChipIcon,
	CurrencyDollarIcon,
	ChartBarIcon,
	PlusIcon,
	ExclamationTriangleIcon,
	CheckCircleIcon,
	ClockIcon,
	PhotoIcon
} from '@heroicons/react/24/outline';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

function Dashboard() {
	// 获取用户信息
	const { data: userInfo } = useQuery({
		queryKey: queryKeys.auth.me,
		queryFn: ApiService.auth.me,
		select: (response) => response.data.user
	});

	// 获取广告系列列表
	const { data: campaigns, isLoading: campaignsLoading } = useQuery({
		queryKey: queryKeys.campaigns.list({}),
		queryFn: () => ApiService.campaigns.list(),
		select: (response) => response.data.campaigns
	});

	// 获取AI统计数据
	const { data: aiStats } = useQuery({
		queryKey: queryKeys.ai.stats({}),
		queryFn: () => ApiService.ai.getStats(),
		select: (response) => response.data.stats
	});

	// 获取Facebook账户
	const { data: fbAccounts } = useQuery({
		queryKey: queryKeys.facebook.accounts,
		queryFn: ApiService.facebook.getAccounts,
		select: (response) => response.data.accounts
	});

	// 计算统计数据
	const stats = React.useMemo(() => {
		if (!campaigns) return null;

		const totalCampaigns = campaigns.length;
		const activeCampaigns = campaigns.filter(c => c.status === 'ACTIVE').length;
		const aiEnabledCampaigns = campaigns.filter(c => c.ai_enabled).length;
		
		const totalSpend = campaigns.reduce((sum, c) => {
			return sum + (c.latestStats?.spend || 0);
		}, 0);

		const totalLikes = campaigns.reduce((sum, c) => {
			return sum + (c.latestStats?.likes || 0);
		}, 0);

		const avgCPL = totalLikes > 0 ? totalSpend / totalLikes : 0;

		return {
			totalCampaigns,
			activeCampaigns,
			aiEnabledCampaigns,
			totalSpend,
			totalLikes,
			avgCPL
		};
	}, [campaigns]);

	// 模拟图表数据（实际项目中应从API获取）
	const chartData = [
		{ date: '12-01', spend: 120, likes: 45, cpl: 2.67 },
		{ date: '12-02', spend: 150, likes: 52, cpl: 2.88 },
		{ date: '12-03', spend: 180, likes: 68, cpl: 2.65 },
		{ date: '12-04', spend: 200, likes: 75, cpl: 2.67 },
		{ date: '12-05', spend: 165, likes: 58, cpl: 2.84 },
		{ date: '12-06', spend: 190, likes: 72, cpl: 2.64 },
		{ date: '12-07', spend: 220, likes: 85, cpl: 2.59 },
	];

	if (campaignsLoading) {
		return (
			<div className="animate-pulse">
				<div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
					{[...Array(4)].map((_, i) => (
						<div key={i} className="bg-white overflow-hidden shadow rounded-lg">
							<div className="p-5">
								<div className="flex items-center">
									<div className="flex-shrink-0">
										<div className="h-8 w-8 bg-gray-200 rounded"></div>
									</div>
									<div className="ml-5 w-0 flex-1">
										<div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
										<div className="h-6 bg-gray-200 rounded w-1/2"></div>
									</div>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-8">
			{/* 页面标题 */}
			<div className="md:flex md:items-center md:justify-between">
				<div className="min-w-0 flex-1">
					<h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
						仪表板
					</h2>
					<p className="mt-1 text-sm text-gray-500">
						欢迎回来，{userInfo?.username}！查看您的广告系列表现概览。
					</p>
				</div>
				<div className="mt-4 flex md:ml-4 md:mt-0">
					<Link
						to="/campaigns/create"
						className="btn-primary"
					>
						<PlusIcon className="h-4 w-4 mr-2" />
						创建广告系列
					</Link>
				</div>
			</div>

			{/* 统计卡片 */}
			<div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
				<div className="bg-white overflow-hidden shadow rounded-lg">
					<div className="p-5">
						<div className="flex items-center">
							<div className="flex-shrink-0">
								<MegaphoneIcon className="h-8 w-8 text-gray-400" />
							</div>
							<div className="ml-5 w-0 flex-1">
								<dl>
									<dt className="text-sm font-medium text-gray-500 truncate">
										总广告系列
									</dt>
									<dd className="text-lg font-medium text-gray-900">
										{stats?.totalCampaigns || 0}
									</dd>
								</dl>
							</div>
						</div>
					</div>
					<div className="bg-gray-50 px-5 py-3">
						<div className="text-sm">
							<span className="text-green-600 font-medium">
								{stats?.activeCampaigns || 0}
							</span>
							<span className="text-gray-500"> 个活跃</span>
						</div>
					</div>
				</div>

				<div className="bg-white overflow-hidden shadow rounded-lg">
					<div className="p-5">
						<div className="flex items-center">
							<div className="flex-shrink-0">
								<CpuChipIcon className="h-8 w-8 text-blue-400" />
							</div>
							<div className="ml-5 w-0 flex-1">
								<dl>
									<dt className="text-sm font-medium text-gray-500 truncate">
										AI 优化
									</dt>
									<dd className="text-lg font-medium text-gray-900">
										{stats?.aiEnabledCampaigns || 0}
									</dd>
								</dl>
							</div>
						</div>
					</div>
					<div className="bg-gray-50 px-5 py-3">
						<div className="text-sm">
							<span className="text-blue-600 font-medium">
								{aiStats?.aiEnabledCampaigns || 0}
							</span>
							<span className="text-gray-500"> 个启用</span>
						</div>
					</div>
				</div>

				<div className="bg-white overflow-hidden shadow rounded-lg">
					<div className="p-5">
						<div className="flex items-center">
							<div className="flex-shrink-0">
								<CurrencyDollarIcon className="h-8 w-8 text-green-400" />
							</div>
							<div className="ml-5 w-0 flex-1">
								<dl>
									<dt className="text-sm font-medium text-gray-500 truncate">
										总花费
									</dt>
									<dd className="text-lg font-medium text-gray-900">
										${(stats?.totalSpend || 0).toFixed(2)}
									</dd>
								</dl>
							</div>
						</div>
					</div>
					<div className="bg-gray-50 px-5 py-3">
						<div className="text-sm">
							<span className="text-gray-500">今日数据</span>
						</div>
					</div>
				</div>

				<div className="bg-white overflow-hidden shadow rounded-lg">
					<div className="p-5">
						<div className="flex items-center">
							<div className="flex-shrink-0">
								<ChartBarIcon className="h-8 w-8 text-purple-400" />
							</div>
							<div className="ml-5 w-0 flex-1">
								<dl>
									<dt className="text-sm font-medium text-gray-500 truncate">
										平均 CPL
									</dt>
									<dd className="text-lg font-medium text-gray-900">
										${(stats?.avgCPL || 0).toFixed(2)}
									</dd>
								</dl>
							</div>
						</div>
					</div>
					<div className="bg-gray-50 px-5 py-3">
						<div className="text-sm">
							<span className="text-purple-600 font-medium">
								{stats?.totalLikes || 0}
							</span>
							<span className="text-gray-500"> 个点赞</span>
						</div>
					</div>
				</div>
			</div>

			{/* 图表区域 */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
				{/* 花费趋势图 */}
				<div className="bg-white shadow rounded-lg p-6">
					<h3 className="text-lg font-medium text-gray-900 mb-4">花费趋势</h3>
					<div className="chart-container">
						<ResponsiveContainer width="100%" height="100%">
							<LineChart data={chartData}>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis dataKey="date" />
								<YAxis />
								<Tooltip 
									formatter={(value, name) => [`$${value}`, '花费']}
									labelFormatter={(label) => `日期: ${label}`}
								/>
								<Line 
									type="monotone" 
									dataKey="spend" 
									stroke="#3b82f6" 
									strokeWidth={2}
									dot={{ fill: '#3b82f6' }}
								/>
							</LineChart>
						</ResponsiveContainer>
					</div>
				</div>

				{/* CPL 趋势图 */}
				<div className="bg-white shadow rounded-lg p-6">
					<h3 className="text-lg font-medium text-gray-900 mb-4">CPL 趋势</h3>
					<div className="chart-container">
						<ResponsiveContainer width="100%" height="100%">
							<BarChart data={chartData}>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis dataKey="date" />
								<YAxis />
								<Tooltip 
									formatter={(value, name) => [`$${value}`, 'CPL']}
									labelFormatter={(label) => `日期: ${label}`}
								/>
								<Bar dataKey="cpl" fill="#10b981" />
							</BarChart>
						</ResponsiveContainer>
					</div>
				</div>
			</div>

			{/* 最近活动和快速操作 */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
				{/* 最近的AI决策 */}
				<div className="bg-white shadow rounded-lg">
					<div className="px-6 py-4 border-b border-gray-200">
						<h3 className="text-lg font-medium text-gray-900">最近的 AI 决策</h3>
					</div>
					<div className="divide-y divide-gray-200">
						{aiStats?.recentActivity?.length > 0 ? (
							aiStats.recentActivity.slice(0, 5).map((activity, index) => (
								<div key={index} className="px-6 py-4 flex items-center space-x-3">
									<div className="flex-shrink-0">
										{activity.decision_type === 'BUDGET_INCREASE' ? (
											<CheckCircleIcon className="h-5 w-5 text-green-500" />
										) : activity.decision_type === 'BUDGET_DECREASE' ? (
											<ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
										) : (
											<ClockIcon className="h-5 w-5 text-gray-400" />
										)}
									</div>
									<div className="flex-1 min-w-0">
										<p className="text-sm font-medium text-gray-900">
											{activity.campaign_name}
										</p>
										<p className="text-sm text-gray-500">
											{getDecisionTypeText(activity.decision_type)}
										</p>
									</div>
									<div className="flex-shrink-0 text-sm text-gray-400">
										{new Date(activity.created_at).toLocaleDateString('zh-CN')}
									</div>
								</div>
							))
						) : (
							<div className="px-6 py-8 text-center">
								<CpuChipIcon className="mx-auto h-12 w-12 text-gray-400" />
								<h3 className="mt-2 text-sm font-medium text-gray-900">暂无 AI 决策</h3>
								<p className="mt-1 text-sm text-gray-500">
									启用 AI 优化后，决策记录将显示在这里
								</p>
							</div>
						)}
					</div>
				</div>

				{/* 快速操作 */}
				<div className="bg-white shadow rounded-lg">
					<div className="px-6 py-4 border-b border-gray-200">
						<h3 className="text-lg font-medium text-gray-900">快速操作</h3>
					</div>
					<div className="p-6 space-y-4">
						<Link
							to="/campaigns/create"
							className="w-full btn-primary justify-center"
						>
							<PlusIcon className="h-4 w-4 mr-2" />
							创建新广告系列
						</Link>
						
						<Link
							to="/facebook-accounts"
							className="w-full btn-secondary justify-center"
						>
							连接 Facebook 账户
						</Link>
						
						<Link
							to="/ai-settings"
							className="w-full btn-secondary justify-center"
						>
							<CpuChipIcon className="h-4 w-4 mr-2" />
							配置 AI 设置
						</Link>
						
						<Link
							to="/upload"
							className="w-full btn-secondary justify-center"
						>
							<PhotoIcon className="h-4 w-4 mr-2" />
							上传广告素材
						</Link>
					</div>
				</div>
			</div>
		</div>
	);
}

// 决策类型文本转换
function getDecisionTypeText(type) {
	const typeMap = {
		'BUDGET_INCREASE': '预算增加',
		'BUDGET_DECREASE': '预算减少',
		'PAUSE_CAMPAIGN': '暂停广告系列',
		'PAUSE_CREATIVE': '暂停创意',
		'RESUME_CAMPAIGN': '恢复广告系列',
		'RESUME_CREATIVE': '恢复创意'
	};
	
	return typeMap[type] || type;
}

export default Dashboard;
